import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import { toast } from 'react-hot-toast';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { PageHeader, Button } from '../../components';
import { agencyService } from '../../services/agencyService';
import { BookingDetail } from './AgencyBookings';
import {
  CARD, errorMessage, localToday, addDays, hoursOf, clockOf, BOOKING_STATE,
} from './shared';

/**
 * Agency > Rota — every active staff member × the week, with their bookings
 * at whichever care homes. Days the agency marked unavailable are hatched, so
 * who is free to offer is visible at a glance. Offers still waiting for the
 * home are drawn dashed.
 */

const TONE = {
  offered: 'border-dashed border-blue-300 bg-white dark:bg-[#262b33]',
  upcoming: 'border-[#D8E4FF] bg-[#EEF3FF] dark:border-blue-900/50 dark:bg-blue-900/20',
  due: 'border-amber-300 bg-amber-50 dark:bg-amber-500/10',
  in_progress: 'border-blue-300 bg-blue-50 dark:bg-blue-500/10',
  completed: 'border-emerald-300 bg-emerald-50/70 dark:bg-emerald-500/10',
  missed: 'border-rose-300 bg-rose-50 dark:bg-rose-500/10',
  no_show: 'border-rose-300 bg-rose-50 dark:bg-rose-500/10',
};

const shortDay = (ymd) => new Date(`${ymd}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
const mondayOf = (ymd) => {
  const d = new Date(`${ymd}T12:00:00`).getDay() || 7;
  return addDays(ymd, 1 - d);
};

const AgencyRota = () => {
  const [week, setWeek] = useState(mondayOf(localToday()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await agencyService.rota(week);
      setData(res.data);
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load the rota.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [week]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const byCell = useMemo(() => {
    const map = new Map();
    (data?.bookings || []).forEach((b) => {
      const key = `${b.worker?.id}|${b.shift_date}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(b);
    });
    return map;
  }, [data]);

  const today = localToday();
  const days = data?.days || [];
  const booked = (data?.bookings || []).filter((b) => b.status !== 'offered');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Rota"
        subtitle="Your staff's shifts at every care home this week. Hatched days are when they told you they are not available."
        actions={(
          <div className="flex items-center gap-2">
            <Button variant="secondary" icon={<ChevronLeft className="w-4 h-4" />} aria-label="Previous week" onClick={() => setWeek(addDays(week, -7))} />
            <Button variant="secondary" leftIcon={<CalendarDays className="w-4 h-4" />} onClick={() => setWeek(mondayOf(localToday()))}>This week</Button>
            <Button variant="secondary" icon={<ChevronRight className="w-4 h-4" />} aria-label="Next week" onClick={() => setWeek(addDays(week, 7))} />
          </div>
        )}
      />
      <div className="flex flex-wrap gap-4 text-[12.5px] text-[#64748B]">
        <span><strong className="text-[#0B1220] dark:text-gray-100">{booked.length}</strong> shifts booked</span>
        <span><strong className="text-[#0B1220] dark:text-gray-100">{new Set(booked.map((b) => b.worker?.id)).size}</strong> of {data?.staff?.length || 0} staff working</span>
        <span><strong className="text-[#0B1220] dark:text-gray-100">{(data?.bookings || []).length - booked.length}</strong> offers waiting for homes</span>
      </div>

      <div className={`${CARD} overflow-x-auto`}>
        <div className="min-w-[1100px]">
          <div className="grid border-b border-[#EDF1F6] dark:border-gray-800" style={{ gridTemplateColumns: '200px repeat(7, minmax(130px, 1fr))' }}>
            <div className="px-4 py-3 text-[11px] font-bold tracking-[0.1em] text-[#62738a] uppercase">Staff</div>
            {days.map((d) => (
              <div key={d} className={`px-2 py-3 text-center text-[11px] font-bold uppercase ${d === today ? 'text-blue-600 bg-[#F2F7FF] dark:bg-blue-500/10' : 'text-[#62738a]'}`}>{shortDay(d)}</div>
            ))}
          </div>
          {!loading && !(data?.staff || []).length && (
            <p className="px-4 py-10 text-center text-[13px] text-[#94A3B8]">Add staff to see them on the rota.</p>
          )}
          {(data?.staff || []).map((s) => (
            <div key={s.id} className="grid border-b border-[#EDF1F6] dark:border-gray-800 min-h-[78px]" style={{ gridTemplateColumns: '200px repeat(7, minmax(130px, 1fr))' }}>
              <div className="px-4 py-3 flex flex-col justify-center">
                <p className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{s.name}</p>
                <p className="text-[11.5px] text-[#94A3B8] truncate">{s.job_role} · {hoursOf(s.hours_minutes)}{!s.accepting_offers ? ' · Paused' : ''}</p>
              </div>
              {days.map((d) => {
                const cell = byCell.get(`${s.id}|${d}`) || [];
                const avail = s.availability?.[d];
                return (
                  <div
                    key={d}
                    className={`p-1.5 flex flex-col gap-1.5 border-l border-[#EDF1F6] dark:border-gray-800 ${avail === 'unavailable' ? 'bg-[repeating-linear-gradient(45deg,#F1F5F9,#F1F5F9_6px,transparent_6px,transparent_12px)] dark:bg-[repeating-linear-gradient(45deg,#2a3038,#2a3038_6px,transparent_6px,transparent_12px)]' : avail === 'not_usual' ? 'bg-[#FAFBFD] dark:bg-[#22262d]' : ''}`}
                    title={avail === 'unavailable' ? 'Marked unavailable' : avail === 'not_usual' ? 'Not one of their usual days' : undefined}
                  >
                    {cell.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setOpen(b)}
                        className={`text-left rounded-lg border px-2 py-1.5 hover:shadow-sm transition ${TONE[b.state] || 'border-[#E2E8F0]'}`}
                      >
                        <p className="text-[11px] font-extrabold text-[#1E293B] dark:text-gray-100">{b.start_time}–{b.end_time}</p>
                        <p className="text-[10.5px] text-[#475569] dark:text-gray-300 truncate">{b.home?.name}</p>
                        <p className="text-[9.5px] font-bold uppercase tracking-wide text-[#64748B] truncate">
                          {b.actual?.clock_in ? `In ${clockOf(b.actual.clock_in)}${b.actual.clock_out ? ` · out ${clockOf(b.actual.clock_out)}` : ''}` : (BOOKING_STATE[b.state]?.label || b.state)}
                        </p>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <BookingDetail booking={open} onClose={() => setOpen(null)} />
    </div>
  );
};

export default AgencyRota;
